package ru.chm.gradereport.ui.screens.course

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import ru.chm.gradereport.model.GradeSheetDto
import ru.chm.gradereport.model.StudentDto
import ru.chm.gradereport.navigation.AppPath
import ru.chm.gradereport.navigation.toHashPath
import ru.chm.gradereport.platform.copyTextToClipboard
import ru.chm.gradereport.platform.currentPageBaseUrl
import ru.chm.gradereport.ui.components.AppCard
import ru.chm.gradereport.ui.components.AppTextField
import ru.chm.gradereport.ui.components.ConfirmDialog
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.PrimaryButton
import ru.chm.gradereport.ui.components.SecondaryButton
import ru.chm.gradereport.ui.components.SoftDeleteButton
import ru.chm.gradereport.ui.theme.GradeTheme

private fun suggestNextStudentNumber(numbers: Iterable<String>): String {
    val maxNumeric = numbers.mapNotNull { it.trim().toIntOrNull() }.maxOrNull()
    val count = numbers.count()
    return ((maxNumeric ?: count) + 1).toString()
}

internal fun studentPublicHashPath(publicToken: String, studentUid: String): String =
    AppPath.PublicStudent(publicToken, studentUid).toHashPath()

internal fun studentPublicUrl(publicToken: String, studentUid: String): String =
    currentPageBaseUrl().trimEnd('/') + "/#${studentPublicHashPath(publicToken, studentUid)}"

@Composable
private fun EditStudentDialog(
    student: StudentDto,
    onDismiss: () -> Unit,
    onConfirm: (fullName: String, studentNumber: String) -> Unit,
) {
    var fullName by remember(student.id) { mutableStateOf(student.fullName) }
    var number by remember(student.id) { mutableStateOf(student.studentNumber) }
    val colors = GradeTheme.colors

    Dialog(onDismissRequest = onDismiss) {
        AppCard(modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp).widthIn(max = 480.dp)) {
            Text(
                "Редактировать студента",
                color = colors.textPrimary,
                fontWeight = FontWeight.Bold,
                fontSize = 20.sp,
            )
            MutedText(
                "UID и публичная ссылка не меняются",
                modifier = Modifier.padding(top = 4.dp, bottom = 10.dp),
            )
            AppTextField(fullName, { fullName = it }, "ФИО", modifier = Modifier.padding(top = 4.dp))
            AppTextField(number, { number = it }, "Номер / зачётка", modifier = Modifier.padding(top = 8.dp))
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                SecondaryButton(text = "Отмена", onClick = onDismiss, modifier = Modifier.weight(1f))
                PrimaryButton(
                    text = "Сохранить",
                    enabled = fullName.isNotBlank(),
                    onClick = { onConfirm(fullName.trim(), number.trim()) },
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun StudentsPanel(
    sheet: GradeSheetDto,
    editable: Boolean,
    onAdd: (String, String) -> Unit,
    onUpdate: (studentId: String, fullName: String, studentNumber: String) -> Unit,
    onDelete: (String) -> Unit,
    onStats: (String) -> Unit,
    onOpenPublicStudent: (studentUid: String) -> Unit = {},
) {
    val colors = GradeTheme.colors
    var fullName by remember { mutableStateOf("") }
    var number by remember {
        mutableStateOf(suggestNextStudentNumber(sheet.students.map { it.studentNumber }))
    }
    var numberEdited by remember { mutableStateOf(false) }
    var nameQuery by remember { mutableStateOf("") }
    var pendingDelete by remember { mutableStateOf<StudentDto?>(null) }
    var editing by remember { mutableStateOf<StudentDto?>(null) }
    var copiedUid by remember { mutableStateOf<String?>(null) }

    val query = nameQuery.trim()
    val visibleStudents = remember(sheet.students, query) {
        sheet.students
            .asSequence()
            .filter { query.isEmpty() || it.fullName.contains(query, ignoreCase = true) }
            .sortedWith(compareBy(String.CASE_INSENSITIVE_ORDER) { it.fullName })
            .toList()
    }

    LaunchedEffect(sheet.students) {
        if (!numberEdited) {
            number = suggestNextStudentNumber(sheet.students.map { it.studentNumber })
        }
    }

    if (editable) {
        AppCard(modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp)) {
            Text("Добавить студента", color = colors.textPrimary, fontWeight = FontWeight.SemiBold)
            AppTextField(fullName, { fullName = it }, "ФИО", modifier = Modifier.padding(top = 8.dp))
            AppTextField(
                number,
                {
                    number = it
                    numberEdited = true
                },
                "Номер / зачётка",
                modifier = Modifier.padding(top = 8.dp),
            )
            PrimaryButton(
                text = "+ Добавить",
                enabled = fullName.isNotBlank(),
                modifier = Modifier.padding(top = 12.dp),
                onClick = {
                    val usedNumber = number.trim()
                    onAdd(fullName.trim(), usedNumber)
                    fullName = ""
                    numberEdited = false
                    number = suggestNextStudentNumber(
                        sheet.students.map { it.studentNumber } + usedNumber,
                    )
                },
            )
        }
    }

    AppTextField(
        value = nameQuery,
        onValueChange = { nameQuery = it },
        label = "Поиск по ФИО",
        modifier = Modifier.padding(bottom = 12.dp),
    )

    if (sheet.students.isEmpty()) {
        MutedText("Нет студентов.")
    } else if (visibleStudents.isEmpty()) {
        MutedText("Нет студентов по запросу «$query».")
    }

    visibleStudents.forEach { student ->
        val publicToken = sheet.course.publicToken
        val canShare = student.uid.isNotBlank() && publicToken.isNotBlank()
        val hashPath = if (canShare) studentPublicHashPath(publicToken, student.uid) else null

        AppCard(modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)) {
            Column(modifier = Modifier.fillMaxWidth()) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top,
                ) {
                    Column(modifier = Modifier.weight(1f).padding(end = 12.dp)) {
                        Text(student.fullName, color = colors.textPrimary, fontWeight = FontWeight.Bold)
                        if (student.studentNumber.isNotBlank()) {
                            MutedText("N ${student.studentNumber}")
                        }
                        if (student.uid.isNotBlank()) {
                            MutedText("UID ${student.uid}")
                        } else {
                            MutedText("UID ещё не назначен — перезапустите сервер")
                        }
                    }
                    if (editable) {
                        SoftDeleteButton(
                            text = "Удалить",
                            onClick = { pendingDelete = student },
                        )
                    }
                }

                if (hashPath != null) {
                    MutedText(
                        "Ссылка для студента: #$hashPath",
                        modifier = Modifier.padding(top = 8.dp),
                    )
                    if (copiedUid == student.uid) {
                        MutedText("Скопировано в буфер обмена", modifier = Modifier.padding(top = 2.dp))
                    }
                }

                FlowRow(
                    modifier = Modifier.padding(top = 10.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    SecondaryButton(text = "Статистика", onClick = { onStats(student.id) })
                    if (editable) {
                        SecondaryButton(text = "Изменить", onClick = { editing = student })
                    }
                    if (canShare) {
                        SecondaryButton(
                            text = "Скопировать ссылку",
                            onClick = {
                                copyTextToClipboard(studentPublicUrl(publicToken, student.uid))
                                copiedUid = student.uid
                            },
                        )
                        SecondaryButton(
                            text = "Открыть страницу",
                            onClick = { onOpenPublicStudent(student.uid) },
                        )
                    }
                }
            }
        }
    }

    editing?.let { student ->
        EditStudentDialog(
            student = student,
            onDismiss = { editing = null },
            onConfirm = { name, studentNumber ->
                onUpdate(student.id, name, studentNumber)
                editing = null
            },
        )
    }

    pendingDelete?.let { student ->
        ConfirmDialog(
            title = "Удалить студента?",
            message = "Студент «${student.fullName}» и все связанные оценки и посещаемость будут удалены.",
            onConfirm = {
                onDelete(student.id)
                pendingDelete = null
            },
            onDismiss = { pendingDelete = null },
        )
    }
}
